import { isPremiumChatLocked, PREMIUM_CHAT_LOCK_MESSAGE } from '../../utils/premiumChatAccess';
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { getModelSupportedInputs } from '../../chatModels';
import FilePreview from './FilePreview';
import InputActions from './InputActions';
import SendButton from './SendButton';
import VoiceToTextButton from './VoiceToTextButton';
import RealtimeVoiceButton from './RealtimeVoiceButton';
import FeaturePills from './FeaturePills';
import ErrorDisplay from '../ui/ErrorDisplay';
import ImageModal from '../ImageModal';
import CustomDropdown from '../dropdown/CustomDropdown';
import { useFileHandling } from '../../hooks/shared/useFileHandling';
import { useMessageSending } from '../../hooks/chat/useMessageSending';
import { useDualMessageSending } from '../../hooks/chat/useDualMessageSending';
import { useModels } from '../../contexts/ModelContext';
import { useImageModal } from '../../hooks/ui/useImageModal';
import ReasoningEffortPicker from './ReasoningEffortPicker';
import {
  getReasoningLevelOptions,
  isReasoningLevelSupported,
} from '../../utils/reasoningLevels';

const MAX_TEXTAREA_HEIGHT = 300;
const MULTILINE_TEXTAREA_THRESHOLD = 50;

const MessageInput = ({
  selectedChat,
  activeProjectId = null,
  projects = [],
  onProjectSelect,
  selectedModel,
  selectedModelName,
  onMessageSent,
  messages = [],
  updateChatTitle,
  hasSubscription,
  hasBasicSubscription,
  messageLimit,
  disableInput = false,
  onUpgrade,
  inputText,
  setInputText,
  isDualMode = false,
  secondaryModel,
  onSecondaryMessageSent,
  secondaryMessages = [],
  editingMessage,
  onCancelEdit,
  applyEditAndTruncate,
  realtimeVoice,
  placeholder = 'Ask anything',
  onModelChange,
  onPinnedModelChange = () => {},
}) => {
  const { models, getModelByModelString } = useModels();
  const isChatLocked = isPremiumChatLocked(selectedChat, [...messages, ...secondaryMessages], models, hasSubscription, [selectedModel, ...(isDualMode ? [secondaryModel] : [])]);
  const { imageModal, openImageModal, closeImageModal } = useImageModal();
  // State
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [reasoningLevel, setReasoningLevel] = useState(null); // null means not selected
  const [isDragActive, setIsDragActive] = useState(false);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [hasSpeechSupport, setHasSpeechSupport] = useState(true);
  const hasRealtimeSupport = realtimeVoice?.isSupported ?? false;
  const isRealtimeConnecting = realtimeVoice?.isConnecting ?? false;
  const isRealtimeConnected = realtimeVoice?.isConnected ?? false;
  const realtimeStatus = realtimeVoice?.status || 'Idle';
  const realtimeError = realtimeVoice?.error || null;
  const startRealtimeSession = realtimeVoice?.startSession;
  const stopRealtimeSession = realtimeVoice?.stopSession;
  const realtimeWeeklyLimitSeconds = realtimeVoice?.weeklyLimitSeconds ?? 5 * 60;
  const realtimeWeeklyRemainingSeconds =
    realtimeVoice?.weeklyRemainingSeconds ?? realtimeWeeklyLimitSeconds;
  const realtimeWeeklyUsageLoaded = realtimeVoice?.weeklyUsageLoaded ?? false;
  const hasReachedRealtimeWeeklyLimit = realtimeVoice?.hasReachedWeeklyLimit ?? false;
  const isInlineEditing = Boolean(editingMessage);
  const isInputDisabled = disableInput || isInlineEditing || isChatLocked;
  const isRealtimeStartBlockedByLimit =
    hasReachedRealtimeWeeklyLimit && !isRealtimeConnecting && !isRealtimeConnected;
  const isRealtimeButtonDisabled = isInputDisabled || isRealtimeStartBlockedByLimit;

  // Custom hooks
  const {
    attachments,
    isThinking,
    isDeepResearch,
    setIsThinking,
    setIsDeepResearch,
    fileInputRef,
    handleFileSelect,
    processFiles,
    removeAttachment,
    clearFileState,
    triggerFileSelect
  } = useFileHandling(selectedModel);

  const {
    handleSendMessage: sendMessage,
    error,
    retryMessage
  } = useMessageSending({
    selectedChat,
    selectedModel,
    selectedModelName,
    onMessageSent,
    messages,
    updateChatTitle,
    hasSubscription,
    messageLimit,
    input,
    setInput,
    setInputText,
    attachments,
    activeProjectId,
    clearFileState,
    isThinking,
    isDeepResearch,
    setIsDeepResearch,
    reasoningLevel: reasoningLevel || 'Auto',
    editingContext: editingMessage,
    onEditComplete: onCancelEdit,
    applyEditAndTruncate
  });

  const {
    handleDualSendMessage: sendDualMessage,
    error: dualError,
    retryMessage: retryDualMessage
  } = useDualMessageSending({
    selectedChat,
    primaryModel: selectedModel,
    secondaryModel: secondaryModel || selectedModel,
    selectedModelName,
    onPrimaryMessageSent: onMessageSent,
    onSecondaryMessageSent: onSecondaryMessageSent || onMessageSent,
    primaryMessages: messages,
    secondaryMessages,
    updateChatTitle,
    hasSubscription,
    messageLimit,
    input,
    setInput,
    setInputText,
    attachments,
    activeProjectId,
    clearFileState,
    isThinking,
    isDeepResearch,
    setIsDeepResearch,
    reasoningLevel: reasoningLevel || 'Auto'
  });

  // Get theme context
  const { isDarkMode } = useTheme();
  const textareaRef = useRef(null);
  const dragCounterRef = useRef(0);
  const recognitionRef = useRef(null);

  // Get model details
  const modelDetails = getModelByModelString(selectedModel);
  const secondaryModelDetails = isDualMode
    ? getModelByModelString(secondaryModel || selectedModel)
    : null;
  const primaryReasoningLevels = useMemo(
    () => getReasoningLevelOptions(modelDetails),
    [modelDetails]
  );
  const secondaryReasoningLevels = useMemo(
    () => getReasoningLevelOptions(secondaryModelDetails),
    [secondaryModelDetails]
  );
  const reasoningLevels = useMemo(() => {
    if (!isDualMode) return primaryReasoningLevels;
    if (primaryReasoningLevels.length === 0 || secondaryReasoningLevels.length === 0) {
      return [];
    }
    const secondaryValues = new Set(secondaryReasoningLevels.map(({ value }) => value));
    return primaryReasoningLevels.filter(({ value }) => secondaryValues.has(value));
  }, [isDualMode, primaryReasoningLevels, secondaryReasoningLevels]);
  const supportedInputs = useMemo(
    () => (modelDetails ? getModelSupportedInputs(modelDetails) || [] : []),
    [modelDetails]
  );

  // Check if any features are active (to determine layout)
  const hasActiveFeatures =
    (isThinking && reasoningLevels.length === 0) || isDeepResearch;
  const useStackedComposerLayout = !selectedChat || hasActiveFeatures;

  const handleReasoningLevelChange = useCallback((level) => {
    setReasoningLevel(level);
    setIsThinking(Boolean(level));
  }, [setIsThinking]);

  useEffect(() => {
    if (isReasoningLevelSupported(reasoningLevel, reasoningLevels)) return;
    setReasoningLevel(null);
    setIsThinking(false);
  }, [reasoningLevel, reasoningLevels, setIsThinking]);

  const composerSurfaceStyle = useMemo(() => {
    const baseShadow = isDarkMode
      ? '0 0 46px 6px rgba(0, 0, 0, 0.28)'
      : '0 0 46px 6px rgba(0, 0, 0, 0.10)';
    const focusShadow = isDarkMode
      ? '0 0 0 2px rgba(255, 255, 255, 0.12), 0 0 46px 6px rgba(0, 0, 0, 0.28)'
      : '0 0 0 2px rgba(0, 0, 0, 0.08), 0 0 46px 6px rgba(0, 0, 0, 0.10)';
    const dragShadow = isDarkMode
      ? '0 0 0 2px rgba(96, 165, 250, 0.55), 0 0 46px 6px rgba(0, 0, 0, 0.28)'
      : '0 0 0 2px rgba(59, 130, 246, 0.45), 0 0 46px 6px rgba(0, 0, 0, 0.10)';

    return {
      backgroundColor: 'var(--color-message-input-background)',
      border: 'none',
      boxShadow: isDragActive ? dragShadow : (isFocused ? focusShadow : baseShadow),
    };
  }, [isDarkMode, isDragActive, isFocused]);

  // Handlers
  const handleKeyDown = (e) => {
    if (isInputDisabled) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isDualMode) {
        sendDualMessage();
      } else {
        sendMessage();
      }
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    setInputText(e.target.value);
  };

  const handleAddMenuActionSelected = useCallback(() => {
    setInput((prev) => {
      if (typeof prev !== 'string' || !prev.startsWith('/')) {
        return prev;
      }
      const next = prev.replace(/^\/+\s*/, '');
      if (next !== prev) {
        setInputText(next);
      }
      return next;
    });
  }, [setInputText]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Handle retry from error display
  const handleRetry = () => {
    if (isDualMode) {
      retryDualMessage();
    } else {
      retryMessage();
    }
  };

  const handleSend = () => {
    if (isInlineEditing) return;
    if (isDualMode) {
      sendDualMessage();
    } else {
      sendMessage();
    }
  };

  const toggleVoiceRecording = () => {
    if (isInputDisabled) return;

    const recognition = recognitionRef.current;
    if (!hasSpeechSupport || !recognition) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (isRecording) {
      recognition.stop();
      return;
    }

    try {
      recognition.start();
      setIsRecording(true);
    } catch (err) {
      // Some browsers throw if start is called twice quickly
      setIsRecording(false);
    }
  };

  const toggleRealtimeVoiceSession = useCallback(async () => {
    if (isInputDisabled) return;
    if (!startRealtimeSession || !stopRealtimeSession) {
      return;
    }
    if (!hasRealtimeSupport) {
      alert('Realtime voice is not supported in this browser.');
      return;
    }

    if (isRealtimeConnecting || isRealtimeConnected) {
      stopRealtimeSession();
      return;
    }

    if (!realtimeWeeklyUsageLoaded) {
      alert('Voice usage is loading. Please try again in a moment.');
      return;
    }

    if (hasReachedRealtimeWeeklyLimit) {
      alert('Weekly voice limit reached (5 minutes).');
      return;
    }

    await startRealtimeSession({
      instructions:
        'You are a concise, helpful voice assistant for the Arrow AI chat app.',
    });
  }, [
    hasRealtimeSupport,
    isInputDisabled,
    isRealtimeConnected,
    isRealtimeConnecting,
    hasReachedRealtimeWeeklyLimit,
    realtimeWeeklyUsageLoaded,
    startRealtimeSession,
    stopRealtimeSession,
  ]);

  const handleDragEnter = (event) => {
    event.preventDefault();
    if (isInputDisabled) return;
    dragCounterRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (isInputDisabled) return;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect =
        supportedInputs.length === 0 ? 'none' : 'copy';
    }
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    if (isInputDisabled) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);

    if (isInputDisabled) return;

    if (supportedInputs.length === 0) {
      alert('File uploading is not supported for this model.');
      return;
    }

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    processFiles(files);
  };

  const handlePaste = useCallback(
    (event) => {
      if (isInputDisabled) return;

      const files = event.clipboardData?.files;
      if (!files || files.length === 0) return;

      event.preventDefault();

      const modelDetails = getModelByModelString(selectedModel);
      const supportedInputs = getModelSupportedInputs(modelDetails);

      if (supportedInputs.length === 0) {
        alert('File uploading is not supported for this model.');
        return;
      }

      processFiles(files);
    },
    [isInputDisabled, processFiles, selectedModel, getModelByModelString]
  );

  const appendTranscript = useCallback((transcript) => {
    if (!transcript) return;
    setInput((prev) => {
      const trimmedPrev = prev.trimEnd();
      const next = trimmedPrev ? `${trimmedPrev} ${transcript}` : transcript;
      setInputText(next);
      return next;
    });
  }, [setInputText]);

  useEffect(() => {
    const SpeechRecognition = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;

    if (!SpeechRecognition) {
      setHasSpeechSupport(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal && result[0]) {
          finalText += result[0].transcript;
        }
      }

      if (finalText.trim()) {
        appendTranscript(finalText.trim());
      }
    };

    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => setIsRecording(false);

    recognitionRef.current = recognition;
    setHasSpeechSupport(true);

    return () => {
      try {
        recognition.stop();
      } catch (err) {
        // Some browsers throw if stop is called after already stopped
      }
    };
  }, [appendTranscript]);

  // Dynamic Textarea Resizing
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const previousHeight = textarea.style.height;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT);
    const nextHeight = `${newHeight}px`;

    if (previousHeight !== nextHeight) {
      textarea.style.height = nextHeight;
    } else {
      textarea.style.height = previousHeight;
    }

    const nextIsMultiLine = newHeight > MULTILINE_TEXTAREA_THRESHOLD;
    setIsMultiLine((current) => (current === nextIsMultiLine ? current : nextIsMultiLine));
  }, [input]);

  // Add effect to update input when inputText changes
  useEffect(() => {
    if (inputText !== input) {
      setInput(inputText);
    }
  }, [inputText, input, setInput]);

  useEffect(() => {
    if (isInputDisabled && isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, [isInputDisabled, isRecording]);

  useEffect(() => {
    if (isInputDisabled && (isRealtimeConnecting || isRealtimeConnected) && stopRealtimeSession) {
      stopRealtimeSession();
    }
  }, [isInputDisabled, isRealtimeConnecting, isRealtimeConnected, stopRealtimeSession]);

  // Check if we can send
  const hasSendableContent =
    !isInlineEditing && (
      input.trim() !== '' ||
      attachments.length > 0
    );
  const canSend = hasSendableContent;
  const showVoiceChatAsPrimaryAction = !hasSendableContent && !isInputDisabled;
  const shouldForceAddMenuOpen = input.startsWith('/');
  const composerError = error || dualError || realtimeError;
  const showRealtimePanel =
    isRealtimeConnecting || isRealtimeConnected;
  const isExpandedComposer =
    !selectedChat ||
    isMultiLine ||
    hasActiveFeatures ||
    showRealtimePanel ||
    attachments.length > 0;
  const composerShapeClass = isExpandedComposer ? 'rounded-[34px]' : 'rounded-full';
  const formatVoiceTime = (seconds) => {
    const clampedSeconds = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(clampedSeconds / 60);
    const remainingSeconds = clampedSeconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };
  const realtimeRemainingLabel = realtimeWeeklyUsageLoaded
    ? `${formatVoiceTime(realtimeWeeklyRemainingSeconds)} left this week`
    : 'Loading voice quota...';
  const realtimeUsageSummaryLabel = realtimeWeeklyUsageLoaded
    ? `${formatVoiceTime(realtimeWeeklyRemainingSeconds)} left of ${formatVoiceTime(realtimeWeeklyLimitSeconds)}`
    : 'Loading voice quota...';
  const realtimeUsageTextClass = hasReachedRealtimeWeeklyLimit
    ? 'text-rose-700 dark:text-rose-200'
    : 'text-emerald-800/80 dark:text-emerald-100/80';
  const realtimeVoiceTooltipText = hasRealtimeSupport
    ? (hasReachedRealtimeWeeklyLimit
      ? 'Use Voice (weekly limit reached)'
      : `Use Voice (${realtimeRemainingLabel})`)
    : 'Realtime voice is not supported in this browser';
  const showComposerModelPicker = Boolean(onModelChange) && !selectedChat && !isDualMode;
  const composerModelPicker = showComposerModelPicker ? (
    <CustomDropdown
      value={selectedModel}
      onChange={onModelChange}
      hasSubscription={hasSubscription}
      onUpgrade={onUpgrade}
      onPinnedModelChange={onPinnedModelChange}
      triggerVariant="composer"
      containerClassName="relative inline-block flex-shrink-0"
    />
  ) : null;
  const reasoningEffortPicker = reasoningLevels.length > 0 ? (
    <ReasoningEffortPicker
      disabled={isInputDisabled}
      level={reasoningLevel}
      levels={reasoningLevels}
      onLevelChange={handleReasoningLevelChange}
      popoverAlign={selectedChat ? 'right' : 'left'}
    />
  ) : null;

  useEffect(() => {
    if (!isChatLocked) return;
    if (isRealtimeConnected || isRealtimeConnecting) stopRealtimeSession?.();
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, [isChatLocked, isRealtimeConnected, isRealtimeConnecting, stopRealtimeSession]);

  return (
    <div className="max-w-4xl w-full mx-auto">
      <div className="pt-4 px-4 pb-4 w-full bg-transparent dark:bg-transparent transition-colors duration-200">
        {/* Error Display */}
        {composerError && (
          <div className="mb-4">
            <ErrorDisplay
              error={composerError}
              onRetry={handleRetry}
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {editingMessage && (
          <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-100">
            <span className="text-sm">Editing inline — finish in the message bubble to resend.</span>
            <button
              type="button"
              className="text-xs font-medium underline"
              onClick={onCancelEdit}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Input Container */}
        <fieldset
          // A new draft must keep its model picker usable so users can switch
          // away from a premium model. Composer actions remain individually disabled.
          disabled={isChatLocked && !showComposerModelPicker}
          aria-label="Message input"
          className={`
            relative
            flex
            flex-col
            theme-message-input
            p-3
            ${composerShapeClass}
            transition-[background-color,border-radius,box-shadow]
            duration-200
            ease-out
          `}
          style={composerSurfaceStyle}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {attachments.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              <FilePreview
                attachments={attachments}
                onRemove={removeAttachment}
                onClearAll={clearFileState}
                onImageClick={openImageModal}
                embedded
              />
            </div>
          )}

          {showRealtimePanel && (
            <div className="mb-3 px-3 py-2 rounded-2xl border border-emerald-200/70 dark:border-emerald-700/70 bg-emerald-50/60 dark:bg-emerald-900/20">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs sm:text-sm text-emerald-800 dark:text-emerald-100">
                  <span className="font-semibold">Voice session:</span> {realtimeStatus}
                </div>
                <button
                  type="button"
                  onClick={stopRealtimeSession}
                  disabled={!stopRealtimeSession}
                  className="px-3 py-1 text-xs rounded-full border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200"
                >
                  End
                </button>
              </div>
              <p className={`mt-2 text-[11px] ${realtimeUsageTextClass}`}>
                <span className="font-medium">Voice time left this week:</span> {realtimeUsageSummaryLabel}
              </p>
            </div>
          )}

          {/* New chats stay two-row; existing chats expand only for active features. */}
          {useStackedComposerLayout ? (
            <>
              {/* Top row: textarea */}
              <textarea
                ref={textareaRef}
                rows="1"
                className="
                  bg-transparent
                  w-full
                  px-3
                  py-2
                  leading-6
                  resize-none
                  overflow-y-auto
                  focus:outline-none
                  text-gray-800
                  dark:text-white
                  placeholder-gray-500
                  dark:placeholder-gray-400
                  text-base
                  transition-[height]
                  duration-150
                  ease-out
                "
                placeholder={isChatLocked ? PREMIUM_CHAT_LOCK_MESSAGE : isInlineEditing ? 'Finish editing above to send' : placeholder}
                value={isChatLocked ? '' : input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={isInputDisabled}
                style={{
                  maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(155,155,155,0.5) transparent',
                }}
              />

              {/* Bottom row: + button, feature pills, model picker, send button */}
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex min-w-0 items-center gap-2">
                  <InputActions
                    modelDetails={modelDetails}
                    isThinking={isThinking}
                    isDeepResearch={isDeepResearch}
                    disableInput={isInputDisabled}
                    onFileSelect={triggerFileSelect}
                    onThinkingToggle={() => setIsThinking(!isThinking)}
                    onDeepResearchToggle={() => setIsDeepResearch(!isDeepResearch)}
                    reasoningLevel={reasoningLevel}
                    onReasoningLevelChange={handleReasoningLevelChange}
                    reasoningLevels={reasoningLevels}
                    forceMenuOpen={shouldForceAddMenuOpen}
                    onMenuActionSelected={handleAddMenuActionSelected}
                    projects={projects}
                    activeProjectId={activeProjectId}
                    onProjectSelect={onProjectSelect}
                  />

                  {!selectedChat && reasoningEffortPicker}

                  <FeaturePills
                    isThinking={isThinking}
                    isDeepResearch={isDeepResearch}
                    reasoningLevels={reasoningLevels}
                    modelDetails={modelDetails}
                    onRemoveThinking={() => setIsThinking(false)}
                    onRemoveDeepResearch={() => setIsDeepResearch(false)}
                  />
                </div>

                <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
                  {composerModelPicker}
                  {selectedChat && reasoningEffortPicker}
                  <VoiceToTextButton
                    onToggle={toggleVoiceRecording}
                    isRecording={isRecording}
                    disabled={isInputDisabled}
                    supported={hasSpeechSupport}
                  />
                  {showVoiceChatAsPrimaryAction ? (
                    <div className="flex items-center gap-2">
                      <RealtimeVoiceButton
                        onToggle={toggleRealtimeVoiceSession}
                        isConnecting={isRealtimeConnecting}
                        isConnected={isRealtimeConnected}
                        disabled={isRealtimeButtonDisabled}
                        supported={hasRealtimeSupport}
                        tooltipText={realtimeVoiceTooltipText}
                      />
                    </div>
                  ) : (
                    <SendButton
                      onClick={handleSend}
                      disabled={isInputDisabled}
                      canSend={canSend}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            /* Layout when no features active - buttons aligned with text */
            <div className={`flex gap-2 ${isMultiLine ? 'items-end' : 'items-center'}`}>
              <div>
                <InputActions
                  modelDetails={modelDetails}
                  isThinking={isThinking}
                  isDeepResearch={isDeepResearch}
                  disableInput={isInputDisabled}
                  onFileSelect={triggerFileSelect}
                  onThinkingToggle={() => setIsThinking(!isThinking)}
                  onDeepResearchToggle={() => setIsDeepResearch(!isDeepResearch)}
                  reasoningLevel={reasoningLevel}
                  onReasoningLevelChange={handleReasoningLevelChange}
                  reasoningLevels={reasoningLevels}
                  forceMenuOpen={shouldForceAddMenuOpen}
                  onMenuActionSelected={handleAddMenuActionSelected}
                  projects={projects}
                  activeProjectId={activeProjectId}
                  onProjectSelect={onProjectSelect}
                />
              </div>

              <textarea
                ref={textareaRef}
                rows="1"
                className="
                  bg-transparent
                  flex-1
                  py-2
                  resize-none
                  overflow-y-auto
                  focus:outline-none
                  text-gray-800
                  dark:text-white
                  placeholder-gray-500
                  dark:placeholder-gray-400
                  text-base
                  min-w-0
                  transition-[height]
                  duration-150
                  ease-out
                "
                placeholder={isChatLocked ? PREMIUM_CHAT_LOCK_MESSAGE : isInlineEditing ? 'Finish editing above to send' : placeholder}
                value={isChatLocked ? '' : input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={isInputDisabled}
                style={{
                  maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(155,155,155,0.5) transparent',
                }}
              />

              <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
                {composerModelPicker}
                {reasoningEffortPicker}
                <VoiceToTextButton
                  onToggle={toggleVoiceRecording}
                  isRecording={isRecording}
                  disabled={isInputDisabled}
                  supported={hasSpeechSupport}
                />
                {showVoiceChatAsPrimaryAction ? (
                  <div className="flex items-center gap-2">
                    <RealtimeVoiceButton
                      onToggle={toggleRealtimeVoiceSession}
                      isConnecting={isRealtimeConnecting}
                      isConnected={isRealtimeConnected}
                      disabled={isRealtimeButtonDisabled}
                      supported={hasRealtimeSupport}
                      tooltipText={realtimeVoiceTooltipText}
                    />
                  </div>
                ) : (
                  <SendButton
                    onClick={handleSend}
                    disabled={isInputDisabled}
                    canSend={canSend}
                  />
                )}
              </div>
            </div>
          )}
        </fieldset>

        <input
          disabled={isChatLocked}
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {disableInput && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">
          {selectedModel === 'DeprecatedModel'
            ? 'This chat uses a deprecated model. Please create a new chat to continue messaging.'
            : 'Message limit reached for this chat. Please create a new chat.'
          }
        </div>
      )}

      <ImageModal
        isOpen={imageModal.isOpen}
        imageUrl={imageModal.imageUrl}
        alt={imageModal.alt}
        startRect={imageModal.startRect}
        onClose={closeImageModal}
      />

    </div>
  );
};

export default MessageInput;
